import { FC, Fragment, useMemo, useState } from 'react';

import { ChevronDownIcon } from '@heroicons/react/outline';
import { SettlementInfo } from '@shared/api/giftItemBrand/giftItemBrand.interface';
import {
  GiftSettlementStatus,
  GiftSupplySettlement,
} from '@shared/api/giftSupplySettlement/giftSupplySettlement.interface';
import { Loading } from '@shared/components/Loading';

import { formatWon, ceilWon } from '../../giftSeller/utils/settlement.utils';
import {
  getLineItemName,
  HistorySortKey,
  SortDirection,
  splitLineSurcharge,
} from '../utils/settlement.utils';
import { SortHeader } from './DeliveredEntriesSection';

// ─────────────────────────────────────────────
// AdminHistoryTable
// ─────────────────────────────────────────────

/** `AdminHistoryTable` 컴포넌트의 props */
export interface AdminHistoryTableProps {
  /** 정산 완료 내역 목록 (undefined이면 로딩 전 상태) */
  historyItems: GiftSupplySettlement[] | undefined;
  /** 데이터 로딩 중 여부 */
  loading: boolean;
  /**
   * 업체 id → `SettlementInfo` 조회 함수.
   * 라인 상세의 surcharge를 소량추가단가/택배비로 분해할 때 사용한다.
   */
  settlementByBrand: (brandId?: string) => SettlementInfo | undefined;
}

/**
 * 관리자 전용 정산 완료 내역 테이블 컴포넌트.
 *
 * - 정산월/정산금액 기준 정렬(오름차순·내림차순)을 지원한다.
 * - 행 클릭 시 라인별 상세(상품/단가/수량/택배비/정산금액)를 펼쳐 보여준다.
 */
export const AdminHistoryTable: FC<AdminHistoryTableProps> = ({
  historyItems,
  loading,
  settlementByBrand,
}) => {
  // ── 정렬 state ──
  const [sort, setSort] = useState<{ key: HistorySortKey; direction: SortDirection }>({
    key: 'month',
    direction: 'desc',
  });
  const toggleSort = (key: HistorySortKey) =>
    setSort((prev) =>
      prev.key === key
        ? { key, direction: prev.direction === 'asc' ? 'desc' : 'asc' }
        : { key, direction: 'desc' },
    );

  // ── 펼치기 state ──
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
  const toggleExpanded = (id: string) =>
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const items = historyItems ?? [];

  const totalCount = useMemo(
    () => items.reduce((sum, s) => sum + s.lines.length, 0),
    [items],
  );
  const totalAmount = useMemo(
    () => items.reduce((sum, s) => sum + s.totalAmount, 0),
    [items],
  );

  const sortedItems = useMemo(() => {
    const sign = sort.direction === 'asc' ? 1 : -1;
    return [...items].sort((a, b) =>
      sort.key === 'amount'
        ? (a.totalAmount - b.totalAmount) * sign
        : (a.year * 12 + a.month - (b.year * 12 + b.month)) * sign,
    );
  }, [items, sort]);

  if (loading) {
    return (
      <div className="center-box py-8">
        <Loading />
      </div>
    );
  }

  if (!items.length) {
    return (
      <div className="rounded-lg bg-white p-8 text-center text-gray-500 shadow">
        정산 내역이 없습니다.
      </div>
    );
  }

  return (
    <div className="overflow-x-auto rounded-lg bg-white shadow">
      <table className="min-w-full divide-y divide-gray-200 text-sm">
        <thead className="bg-gray-50 text-xs text-gray-500">
          <tr>
            <th className="px-3 py-2 text-left">업체</th>
            <SortHeader
              label="정산월"
              active={sort.key === 'month'}
              direction={sort.direction}
              onClick={() => toggleSort('month')}
            />
            <th className="px-3 py-2 text-left">상태</th>
            <th className="px-3 py-2 text-left">지급일</th>
            <th className="px-3 py-2 text-right">건수</th>
            <SortHeader
              label="정산금액(VAT 포함)"
              active={sort.key === 'amount'}
              direction={sort.direction}
              align="right"
              onClick={() => toggleSort('amount')}
            />
            <th className="px-3 py-2 text-center">상세내역</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {sortedItems.map((s) => {
            const expanded = expandedIds.has(s._id);
            const brandId =
              typeof s.giftItemBrand === 'object' ? s.giftItemBrand?._id : s.giftItemBrand;
            const brandSettlementInfo = settlementByBrand(brandId);
            return (
              <Fragment key={s._id}>
                <tr
                  className="cursor-pointer hover:bg-gray-50"
                  onClick={() => toggleExpanded(s._id)}
                >
                  <td className="whitespace-nowrap px-3 py-3 text-xs font-medium text-gray-700">
                    {typeof s.giftItemBrand === 'object' && s.giftItemBrand?.name ? (
                      s.giftItemBrand.name
                    ) : (
                      <span className="text-gray-300">-</span>
                    )}
                  </td>
                  <td className="whitespace-nowrap px-3 py-3 font-medium">
                    {s.year}년 {s.month}월
                  </td>
                  <td className="px-3 py-3">
                    <span
                      className={`rounded-full px-2 py-0.5 text-xs font-medium ${
                        s.status === GiftSettlementStatus.Paid
                          ? 'bg-green-50 text-green-700'
                          : 'bg-amber-50 text-amber-700'
                      }`}
                    >
                      {s.status === GiftSettlementStatus.Paid ? '지급완료' : '정산예정'}
                    </span>
                  </td>
                  <td className="whitespace-nowrap px-3 py-3 text-sm text-gray-900">
                    {s.executedAt ? (
                      <div className="flex items-center gap-1.5">
                        <span className="rounded-full bg-green-100 px-2 py-0.5 text-xs font-semibold text-green-700">
                          완료
                        </span>
                        {new Date(s.executedAt).toLocaleDateString('ko-KR')}
                      </div>
                    ) : (
                      <div className="flex items-center gap-1.5">
                        <span className="rounded-full bg-indigo-100 px-2 py-0.5 text-xs font-semibold text-indigo-600">
                          예정
                        </span>
                        {s.month === 12 ? s.year + 1 : s.year}년{' '}
                        {s.month === 12 ? 1 : s.month + 1}월 15일
                      </div>
                    )}
                  </td>
                  <td className="whitespace-nowrap px-3 py-3 text-right tabular-nums text-gray-600">
                    {s.lines.length}건
                  </td>
                  <td className="whitespace-nowrap px-3 py-3 text-right font-semibold tabular-nums text-gray-800">
                    {formatWon(s.totalAmount)}
                  </td>
                  <td className="px-3 py-3 text-center text-xs text-gray-400">
                    <span className="inline-flex items-center gap-1">
                      상세보기
                      <ChevronDownIcon
                        className={`h-4 w-4 transition-transform duration-200 ${
                          expanded ? 'rotate-180' : ''
                        }`}
                      />
                    </span>
                  </td>
                </tr>

                {/* 라인별 상세 펼침 영역 */}
                {expanded && (
                  <tr>
                    <td colSpan={7} className="bg-gray-50 px-3 py-3">
                      <table className="min-w-full divide-y divide-gray-200 text-xs">
                        <thead className="text-gray-500">
                          <tr>
                            <th className="px-3 py-1.5 text-left">상품</th>
                            <th className="px-3 py-1.5 text-right">단가(+소량)</th>
                            <th className="px-3 py-1.5 text-right">× 수량</th>
                            <th className="px-3 py-1.5 text-right">+ 택배비</th>
                            <th className="px-3 py-1.5 text-right">= 정산금액(VAT 포함)</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100 bg-white">
                          {s.lines.map((line, idx) => {
                            const { extraUnitAmount, courierFee } = splitLineSurcharge(
                              line.quantity,
                              line.surcharge,
                              brandSettlementInfo,
                            );
                            const extraUnitPrice =
                              line.quantity > 0 ? extraUnitAmount / line.quantity : 0;
                            return (
                              <tr key={line.giftDelivery?.toString() ?? idx}>
                                <td className="px-3 py-2">
                                  <p className="max-w-[200px] truncate font-medium">
                                    {getLineItemName(line)}
                                  </p>
                                </td>
                                <td className="whitespace-nowrap px-3 py-2 text-right tabular-nums">
                                  {line.unitAmount.toLocaleString()}
                                  {extraUnitPrice > 0 && (
                                    <span className="text-amber-600">
                                      (+{extraUnitPrice.toLocaleString()})
                                    </span>
                                  )}
                                </td>
                                <td className="whitespace-nowrap px-3 py-2 text-right tabular-nums">
                                  {line.quantity}개
                                </td>
                                <td className="whitespace-nowrap px-3 py-2 text-right tabular-nums">
                                  {courierFee > 0 ? (
                                    courierFee.toLocaleString()
                                  ) : (
                                    <span className="text-gray-300">-</span>
                                  )}
                                </td>
                                <td className="whitespace-nowrap px-3 py-2 text-right font-semibold tabular-nums text-gray-800">
                                  {formatWon(line.lineAmount)}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </td>
                  </tr>
                )}
              </Fragment>
            );
          })}
        </tbody>
        <tfoot className="bg-gray-50 text-sm font-semibold">
          <tr>
            <td className="px-3 py-3 text-gray-500" colSpan={3}>
              합계 ({items.length}건)
            </td>
            <td />
            <td className="whitespace-nowrap px-3 py-3 text-right tabular-nums text-gray-500">
              {totalCount}건
            </td>
            <td className="whitespace-nowrap px-3 py-3 text-right tabular-nums text-rose-700">
              {formatWon(ceilWon(totalAmount))}
            </td>
            <td />
          </tr>
        </tfoot>
      </table>
    </div>
  );
};
